import { c as _c } from "react/compiler-runtime";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { RolePartiallyChanged } from "@components/table/members/members-column/role-column/role-partially-changed";
import { useToast } from "@flat-ui";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { updateMemberRoleInProjectSingle } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { formatUserRoleType } from "@utils/data-display";
import { GroupMemberEvents, ProjectMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/**
 * Hook for changing a single member's role.
 * Dispatches the appropriate thunk based on subject type and shows toast on success.
 * Reusable across different UI patterns (table cell dropdown, 3-dot menu, etc.).
 */
export function useChangeMemberRole(t0) {
  const $ = _c(11);
  const {
    companyId,
    subjectType,
    memberIdentity,
    currentRole,
    selectedProject,
    group,
    location
  } = t0;
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  let t1;
  if ($[0] !== companyId || $[1] !== currentRole || $[2] !== dispatch || $[3] !== group || $[4] !== location || $[5] !== memberIdentity || $[6] !== openToast || $[7] !== selectedProject || $[8] !== subjectType || $[9] !== trackEvent) {
    const changeMemberRole = async function changeMemberRole(newRole) {
      if (newRole === currentRole) {
        return;
      }
      if (subjectType === "project" && selectedProject) {
        await changeProjectRole(newRole);
        return;
      }
      if (subjectType === "group" && group) {
        await changeGroupRole(newRole);
        return;
      }
      if (subjectType === "workspace" || subjectType === "team") {
        await changeCompanyRole(newRole);
      }
    };
    async function changeProjectRole(role) {
      trackEvent({
        name: ProjectMemberEvents.changeRole,
        props: {
          role,
          location
        }
      });
      await dispatch(updateMemberRoleInProjectSingle({
        companyId,
        projectId: selectedProject.id,
        userIdentity: memberIdentity,
        payload: {
          role
        }
      })).unwrap();
      openToast({
        title: "Role Changed",
        message: <RoleChangeSuccessDescription role={formatUserRoleType(role)} />
      });
    }
    async function changeGroupRole(role_0) {
      trackEvent({
        name: GroupMemberEvents.changeRole,
        props: {
          role: role_0,
          location
        }
      });
      await dispatch(updateMemberRoleInGroup({
        companyId,
        groupId: group.id,
        role: role_0,
        userIdentity: memberIdentity
      })).unwrap();
      openToast({
        title: "Role Changed",
        message: <RoleChangeSuccessDescription role={formatUserRoleType(role_0)} />
      });
    }
    async function changeCompanyRole(role_1) {
      const result = await dispatch(updateMemberRoleInWorkspace({
        companyId,
        role: role_1,
        identity: memberIdentity
      })).unwrap();
      if (result.role !== role_1) {
        const newRoleName = formatUserRoleType(result.role);
        const roleSubject = result.role === "COMPANY_MANAGER" || result.role === "PROJECT_MANAGER" ? "group" : "team";
        openToast({
          title: <>Role Changed to <strong>{newRoleName}</strong></>,
          message: <RolePartiallyChanged intendedRole={formatUserRoleType(role_1)} newRole={newRoleName} roleSubject={roleSubject} />,
          variant: "warning"
        });
      } else {
        openToast({
          title: "Role Changed",
          message: <RoleChangeSuccessDescription role={formatUserRoleType(role_1)} />
        });
      }
    }
    t1 = {
      changeMemberRole
    };
    $[0] = companyId;
    $[1] = currentRole;
    $[2] = dispatch;
    $[3] = group;
    $[4] = location;
    $[5] = memberIdentity;
    $[6] = openToast;
    $[7] = selectedProject;
    $[8] = subjectType;
    $[9] = trackEvent;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}